import {ConfigProvider, Image} from 'antd';
import {ReactElement, useCallback, useMemo, useState} from 'react';

import {PREVIEW_IMAGE_PLACEHOLDER_SRC, usePreviewVideoController} from '../../../../review-preview-video';
import {applyPreviewVideoVisibleChange} from '../../../../review-preview-video-types';

import {GalleryMediaItemType, isGalleryVideoMediaItem} from './preview-group-media';

type PropsType = {
    initialIndex: number;
    mediaItems: Array<GalleryMediaItemType>;
    onClose: () => void;
};

function buildPreviewGroupItems(items: Array<GalleryMediaItemType>): Array<{alt?: string; src: string}> {
    return items.map((item) => {
        if (item.type === 'image') {
            return item.alt
                ? {
                      alt: item.alt,
                      src: item.src,
                  }
                : {
                      src: item.src,
                  };
        }

        return {
            alt: '',
            src: item.poster ?? PREVIEW_IMAGE_PLACEHOLDER_SRC,
        };
    });
}

export function GalleryPreviewOverlay(props: PropsType): ReactElement {
    const {initialIndex, mediaItems, onClose} = props;
    const {previewClassName, renderEmptyPreviewToolbar, renderVideo, stop} = usePreviewVideoController();
    const [previewOpen, setPreviewOpen] = useState(true);
    const [current, setCurrent] = useState(initialIndex);

    const handlePreviewVisibleChange = useCallback(
        (visible: boolean) => {
            applyPreviewVideoVisibleChange(visible, {
                setOpen: (open) => {
                    setPreviewOpen(open);

                    if (!open) {
                        onClose();
                    }
                },
                stop,
            });
        },
        [onClose, stop]
    );

    const previewItems = useMemo(() => buildPreviewGroupItems(mediaItems), [mediaItems]);

    const groupPreview = useMemo(
        () => ({
            className: previewClassName,
            current,
            imageRender: (originalNode: ReactElement, info: {current: number}) => {
                const item = mediaItems[info.current];

                if (isGalleryVideoMediaItem(item)) {
                    if (!previewOpen) {
                        return null;
                    }

                    return renderVideo(item.src, item.embedType);
                }

                return originalNode;
            },
            onChange: (nextCurrent: number) => {
                stop();
                setCurrent(nextCurrent);
            },
            onVisibleChange: handlePreviewVisibleChange,
            toolbarRender: (originalNode: ReactElement, info: {current: number}) => {
                const item = mediaItems[info.current];

                if (isGalleryVideoMediaItem(item)) {
                    return renderEmptyPreviewToolbar();
                }

                return originalNode;
            },
            visible: previewOpen,
        }),
        [
            current,
            handlePreviewVisibleChange,
            mediaItems,
            previewClassName,
            previewOpen,
            renderEmptyPreviewToolbar,
            renderVideo,
            stop,
        ]
    );

    return (
        <ConfigProvider
            locale={{
                locale: '',
                Image: {preview: ''},
            }}
        >
            <Image.PreviewGroup items={previewItems} preview={groupPreview} />
        </ConfigProvider>
    );
}
